import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangleIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { formatCount, formatDate, orDash } from "~/lib/format";
import { useFeeScheduleGaps, useMerchants, type FeeScheduleGap } from "~/lib/queries";
import { BackfillDialog } from "./-backfill-dialog";

interface MerchantSearch {
  search?: string;
  limit?: number;
  offset?: number;
}

export const Route = createFileRoute("/_authed/merchants/")({
  validateSearch: (raw: Record<string, unknown>): MerchantSearch => ({
    search: typeof raw.search === "string" && raw.search.trim() !== "" ? raw.search : undefined,
    limit: positive(raw.limit),
    offset: positive(raw.offset),
  }),
  component: MerchantsPage,
});

function positive(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

const COLUMNS = 5;

/**
 * The legal counterparties above storefronts (PRO-993), and the one place to
 * see whether every merchant can actually be settled (PRO-1200/1202).
 *
 * The banner is the point of the page as much as the table: a merchant with
 * payments in a method and no fee schedule for it will have its next
 * settlement refused, and before this screen the first anyone heard of it was
 * a 500 in the settlement webhook's logs.
 */
function MerchantsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = {
    search: search.search,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
  const { data, error, isPending, isFetching, refetch } = useMerchants(filters);

  function patch(next: Partial<MerchantSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  const rows = data?.items ?? [];
  const searching = search.search !== undefined;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Merchants"
        description="The businesses behind the storefronts: who Kugie owes, and what it charges them."
      />

      <GapsBanner />

      <DebouncedInput
        value={search.search ?? ""}
        onCommit={(value) => patch({ search: value || undefined })}
        placeholder="Search merchant name…"
        className="max-w-sm"
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Storefronts</TableHead>
              <TableHead>Bank account</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError columns={COLUMNS} message={errorMessage(error)} onRetry={() => void refetch()} />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={searching ? "No merchants match this search." : "No merchants yet."}
                onClear={searching ? () => patch({ search: undefined }) : undefined}
              />
            ) : (
              rows.map((merchant) => (
                <TableRow key={merchant.merchant_id}>
                  <TableCell className="max-w-[18rem] truncate font-medium">
                    <Link
                      to="/merchants/$merchantId"
                      params={{ merchantId: merchant.merchant_id }}
                      className="text-primary hover:underline"
                    >
                      {merchant.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={merchant.status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(merchant.storefront_count)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {merchant.has_bank_account
                      ? `${orDash(merchant.bank_code)} ••••${merchant.bank_account_last4 ?? ""}`
                      : "not set"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(merchant.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar pagination={data.pagination} onChange={(next) => patch(next)} disabled={isFetching} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Shown only when something is wrong. A banner that is always there, saying
 * "all good", is a banner nobody reads on the day it changes.
 */
function GapsBanner() {
  const { data: gaps } = useFeeScheduleGaps();
  const [open, setOpen] = useState(false);

  if (!gaps || gaps.length === 0) return null;

  const merchants = new Set(gaps.map((g) => g.merchant_id)).size;
  const waiting = gaps.reduce((sum, g) => sum + g.unsettled_count, 0);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
      <div className="flex min-w-0 items-start gap-3">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-warning" />
        <div className="space-y-1 text-sm">
          <p className="font-medium">
            {merchants} merchant{merchants === 1 ? "" : "s"} can't be settled: no fee schedule for{" "}
            {describeGaps(gaps)}.
          </p>
          <p className="text-muted-foreground">
            {waiting > 0
              ? `${formatCount(waiting)} payment${waiting === 1 ? " is" : "s are"} waiting to settle and will be refused until a schedule covers them.`
              : "Their next settlement will be refused until a schedule covers it."}
          </p>
        </div>
      </div>
      <Button size="sm" onClick={() => setOpen(true)}>
        Backfill fee schedules
      </Button>
      {open ? <BackfillDialog open={open} onOpenChange={setOpen} gaps={gaps} /> : null}
    </div>
  );
}

function describeGaps(gaps: FeeScheduleGap[]): string {
  const shown = gaps.slice(0, 3).map((g) => `${g.merchant_name} (${g.payment_method})`);
  return gaps.length > 3 ? `${shown.join(", ")} and ${gaps.length - 3} more` : shown.join(", ");
}
